import { useState } from "react";
import { PrimaryButtonSmall } from "./Button";
import { showToast } from "../utils/showToast";
import PayoutFields, { configToForm, formToIntroRate, formToMinPayout, describeSectionWindow } from "./PayoutFields";

/** One-line summary of a saved per-host override (which sections are custom vs inherited). */
export function describeOverride(ov) {
  if (!ov) return "—";
  const rateOverridden = ov.hostRate != null || ov.introRate != null;
  const minOverridden = ov.minPayout != null;
  const parts = [];
  if (rateOverridden) {
    const pct = ov.hostRate != null ? Math.round(ov.hostRate * 1000) / 10 : "?";
    const intro = ov.introRate;
    if (intro && intro.enabled) {
      parts.push(`${Math.round(intro.rate * 1000) / 10}% for ${describeSectionWindow(intro)}, then ${pct}%`);
    } else {
      parts.push(`${pct}% host share`);
    }
  } else {
    parts.push("rate: global");
  }
  if (minOverridden) {
    const min = ov.minPayout;
    if (min && min.enabled) {
      parts.push(`$${(min.amountCents || 0) / 100} min on ${describeSectionWindow(min)}`);
    } else {
      parts.push("min: none");
    }
  } else {
    parts.push("min: global");
  }
  if (ov.autoPayout != null) {
    parts.push(ov.autoPayout.enabled ? "auto payouts: on" : "auto payouts: off");
  }
  if (typeof ov.guestShareRate === "number") {
    parts.push(`guest share: ${Math.round(ov.guestShareRate * 1000) / 10}%`);
  }
  return parts.join(" · ");
}

/** Plain-English description of the rate a host inherits from the global config. */
export function describeInheritedRate(cfg) {
  // No literal fallback: the split is configurable, so guessing 70% here would
  // state a rate the platform may not actually pay (GIS-194).
  const base = cfg && cfg.hostRate != null ? `${Math.round(cfg.hostRate * 1000) / 10}%` : "—";
  const intro = cfg && cfg.introRate;
  if (intro && intro.enabled) {
    return `${Math.round(intro.rate * 1000) / 10}% for ${describeSectionWindow(intro)}, then ${base}`;
  }
  return base;
}

/** Plain-English description of the minimum guarantee a host inherits from the global config. */
export function describeInheritedMin(cfg) {
  const min = cfg && cfg.minPayout;
  if (!min || !min.enabled) return "no minimum";
  return `$${(min.amountCents || 0) / 100} min on ${describeSectionWindow(min)} (${min.fundingMode})`;
}

// Matches MAX_SHARE_RATE in the functions repo's users/guestPayouts/guestShare.js.
const MAX_GUEST_SHARE_PCT = 25;

/** Plain-English description of the guest share a host inherits from the global config. */
export function describeInheritedGuestShare(cfg) {
  const rate = cfg && cfg.guestPayouts && cfg.guestPayouts.shareRate;
  // No literal fallback for the same reason describeInheritedRate has none: the
  // server's built-in default is not something this screen should assert.
  return typeof rate === "number" ? `${Math.round(rate * 1000) / 10}%` : "the platform default";
}

/** Plain-English description of the payout mode a host inherits from the global config. */
export function describeInheritedAuto(cfg) {
  return cfg && cfg.autoPayout && cfg.autoPayout.enabled ? "automatic" : "manual";
}

/**
 * The per-host payout override editor. The host can override the rate section,
 * the minimum-guarantee section, or both; each inherits the global config when
 * its checkbox is off. `onSave(override)` receives the wire override object (or
 * null to clear). Stateless about persistence — the parent owns the callable.
 */
export default function PayoutOverrideForm({ vendorID, globalCfg, initialOverride, onSave, onCancel, saving }) {
  const ov = initialOverride;
  const [overrideRate, setOverrideRate] = useState(!!(ov && (ov.hostRate != null || ov.introRate != null)));
  const [overrideMin, setOverrideMin] = useState(!!(ov && ov.minPayout != null));
  // Automatic payouts is a THREE-state choice, not a checkbox behind a checkbox:
  // inherit the platform default, always pay this host automatically, or never.
  // A nested checkbox pre-seeded from the global value looked like "no change"
  // while silently writing an explicit override, which would then survive (or
  // defy) a later change to the platform default.
  const [autoMode, setAutoMode] = useState(
    ov?.autoPayout == null ? "inherit" : (ov.autoPayout.enabled ? "on" : "off")
  );
  // The slice of this host's events that can be spent on guest appearance fees.
  // Its own control rather than a field on the rate section, because it is a
  // different pot: the host's share is untouched by it either way.
  const [overrideGuestShare, setOverrideGuestShare] = useState(typeof ov?.guestShareRate === "number");
  const [guestSharePct, setGuestSharePct] = useState(
    typeof ov?.guestShareRate === "number" ? String(Math.round(ov.guestShareRate * 1000) / 10) : ""
  );
  const [form, setForm] = useState(configToForm({
    hostRate: ov?.hostRate != null ? ov.hostRate : globalCfg?.hostRate,
    introRate: ov?.introRate != null ? ov.introRate : globalCfg?.introRate,
    minPayout: ov?.minPayout != null ? ov.minPayout : globalCfg?.minPayout,
  }));

  const handleSave = () => {
    if (overrideRate) {
      const pct = Number(form.hostRatePct);
      if (!Number.isFinite(pct) || pct < 0 || pct > 100) { showToast("Host share must be between 0 and 100%."); return; }
      if (form.introEnabled) {
        const ipct = Number(form.introRatePct);
        if (!Number.isFinite(ipct) || ipct < 0 || ipct > 100) { showToast("Boosted host share must be between 0 and 100%."); return; }
        if (!(Number(form.introFirstNEvents) >= 1)) { showToast("Boost: first N events must be at least 1."); return; }
      }
    }
    if (overrideMin && form.minEnabled) {
      if (!(Number(form.minAmountDollars) > 0)) { showToast("Minimum payout must be greater than $0."); return; }
      if (!(Number(form.firstNEvents) >= 1)) { showToast("First N events must be at least 1."); return; }
    }
    const sharePct = overrideGuestShare ? Number(guestSharePct) : null;
    if (overrideGuestShare && (!Number.isFinite(sharePct) || sharePct < 0 || sharePct > MAX_GUEST_SHARE_PCT)) {
      showToast(`Guest share must be between 0 and ${MAX_GUEST_SHARE_PCT}%.`);
      return;
    }
    // Every section inheriting means there is no deal left to keep, so the whole
    // override is cleared. guestShareRate has to be part of that test: without
    // it, opening a host who only has a custom guest share and saving would
    // clear it while looking like it changed nothing.
    const override = (!overrideRate && !overrideMin && !overrideGuestShare && autoMode === "inherit")
      ? null
      : {
          hostRate: overrideRate ? Number(form.hostRatePct) / 100 : null,
          introRate: overrideRate ? formToIntroRate(form, { allowCountAnchor: true }) : null,
          minPayout: overrideMin ? formToMinPayout(form, { allowCountAnchor: true }) : null,
          autoPayout: autoMode === "inherit" ? null : { enabled: autoMode === "on" },
          // Always sent, so this form is the authority on it: null means inherit.
          guestShareRate: overrideGuestShare ? sharePct / 100 : null,
        };
    onSave(override);
  };

  return (
    <div className="mt-3 space-y-4 border-t border-gray-100 dark:border-gray-800 pt-3">
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
        <label className="flex items-center gap-x-3">
          <input
            type="checkbox"
            checked={overrideRate}
            onChange={(e) => setOverrideRate(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Override host share (inheriting {describeInheritedRate(globalCfg)})
          </span>
        </label>
        {overrideRate && (
          <div className="mt-4">
            <PayoutFields value={form} onChange={setForm} idPrefix={`ov-rate-${vendorID}`} showMin={false} allowCountAnchor />
          </div>
        )}
      </div>
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
        <label className="flex items-center gap-x-3">
          <input
            type="checkbox"
            checked={overrideMin}
            onChange={(e) => setOverrideMin(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Override minimum guarantee (inheriting {describeInheritedMin(globalCfg)})
          </span>
        </label>
        {overrideMin && (
          <div className="mt-4">
            <PayoutFields value={form} onChange={setForm} idPrefix={`ov-min-${vendorID}`} showRate={false} allowCountAnchor />
          </div>
        )}
      </div>
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
        <label className="flex items-center gap-x-3">
          <input
            type="checkbox"
            checked={overrideGuestShare}
            onChange={(e) => setOverrideGuestShare(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Override guest share (inheriting {describeInheritedGuestShare(globalCfg)})
          </span>
        </label>
        {overrideGuestShare && (
          <div className="mt-3">
            <label htmlFor={`ov-guest-${vendorID}`} className="block text-sm text-gray-700 dark:text-gray-200">
              Share of this host's events that can go to guests
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                id={`ov-guest-${vendorID}`}
                type="number"
                min="0"
                max={MAX_GUEST_SHARE_PCT}
                step="1"
                value={guestSharePct}
                onChange={(e) => setGuestSharePct(e.target.value)}
                placeholder="10"
                className="w-24 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
            </div>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Comes out of the platform's side, never the host's. Gist keeps at least 5%, so a
              boosted host share leaves less than this for guests. Max {MAX_GUEST_SHARE_PCT}%.
            </p>
          </div>
        )}
      </div>
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">Automatic payouts</span>
        <div className="mt-2 space-y-1.5">
          {[
            { v: "inherit", label: `Use the platform default (${describeInheritedAuto(globalCfg)})`, hint: "Follows the global setting, including if it changes later." },
            { v: "on", label: "Always pay this host automatically", hint: "Their matured funds go out daily even if the platform default is manual." },
            { v: "off", label: "Never pay this host automatically", hint: "Hold this host out of the daily run; pay them by hand." },
          ].map((opt) => (
            <label key={opt.v} className="flex cursor-pointer items-start gap-x-2.5 rounded-md p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700">
              <input
                type="radio"
                name={`ov-auto-${vendorID}`}
                checked={autoMode === opt.v}
                onChange={() => setAutoMode(opt.v)}
                className="mt-1 h-4 w-4 border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
              />
              <span>
                <span className="block text-sm text-gray-800 dark:text-gray-200">{opt.label}</span>
                <span className="block text-xs text-gray-400 dark:text-gray-500">{opt.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
          Cancel
        </button>
        <PrimaryButtonSmall onClick={handleSave} loading={saving} loadingLabel="Saving…">
          {!overrideRate && !overrideMin && autoMode === "inherit" ? "Clear override" : "Save override"}
        </PrimaryButtonSmall>
      </div>
    </div>
  );
}
